// FILE: src/components/admin/AdminQuickActions.js
//
// Shared floating Quick Actions footer for ALL admin screens.
//
// Extracted from AdminScreen (Mission Control) so every admin tool carries
// the same one-click navigation ribbon. Mounted in two mutually exclusive
// places:
//   1. AdminScreen renders it on the Mission Control hub (activeView=null),
//      delegating clicks to its legacy onOpen* props via QUICK_ACTION_TOOLS'
//      cb field.
//   2. App.js renders it beside every admin sub-view (activeView=adminView),
//      routing clicks through handleQuickAction.
//
// The admin-open-* testids are preserved exactly — the jest suites and
// smoke.spec.js drive admin navigation through them. Only one instance is
// ever mounted at a time, so the testids stay unique.

import React, { useMemo } from 'react';
import { IS_STAGING } from '../../services/supabaseClient';
import { isFeatureEnabled } from '../../config/featureFlags';

// toolId → App.js adminView value; used for the active-tool highlight.
// 'hub' maps to null (Mission Control renders when adminView is null).
const VIEW_BY_TOOL = {
  hub: null,
  questionFactory: 'question-factory',
  reportedQuestions: 'reported-questions',
  editor: 'editor',
  bulkImport: 'bulk-import',
  dashboard: 'dashboard',
  questionPerformance: 'question-performance',
  telemetry: 'telemetry',
  emailAnalytics: 'email-analytics',
  journeyActivation: 'journey-activation',
  badgeManager: 'badge-manager',
  adminUsers: 'admin-users',
  registeredUsers: 'registered-users',
  foundingTesters: 'founding-testers-admin',
  testimonials: 'testimonials',
  caseStudies: 'case-studies',
  seoHealth: 'seo-health',
  releaseNotes: 'release-notes',
  feedbackHub: 'feedback',
  dataTools: 'data-tools',
};

// Every admin tool, in ribbon order. cb is the AdminScreen onOpen* prop name
// (the hub's legacy delegation path); testids are load-bearing for tests.
export const QUICK_ACTION_TOOLS = [
  { id: 'hub', label: 'Mission Control', icon: '\u{2699}\u{FE0F}', testId: 'admin-open-hub', cb: null },
  { id: 'questionFactory', label: 'Generate', icon: '➕', testId: 'admin-open-question-factory', cb: 'onOpenQuestionFactory', primary: true },
  { id: 'reportedQuestions', label: 'Review Reports', icon: '\u{1F6A9}', testId: 'admin-open-reported-questions', cb: 'onOpenReportedQuestions', badge: true },
  { id: 'editor', label: 'Editor', icon: '✏️', testId: 'admin-open-editor', cb: 'onOpenEditor' },
  { id: 'bulkImport', label: 'Bulk Import', icon: '\u{1F4E5}', testId: 'admin-open-bulk-import', cb: 'onOpenBulkImport' },
  { id: 'dashboard', label: 'Dashboard', icon: '\u{1F4CA}', testId: 'admin-open-dashboard', cb: 'onOpenDashboard' },
  { id: 'questionPerformance', label: 'Performance', icon: '\u{1F4C8}', testId: 'admin-open-question-performance', cb: 'onOpenQuestionPerformance' },
  { id: 'telemetry', label: 'Telemetry', icon: '\u{1F4E1}', testId: 'admin-open-telemetry', cb: 'onOpenTelemetry' },
  { id: 'emailAnalytics', label: 'Email', icon: '\u{1F4E7}', testId: 'admin-open-email-analytics', cb: 'onOpenEmailAnalytics' },
  { id: 'journeyActivation', label: 'Activation', icon: '\u{1F680}', testId: 'admin-open-journey-activation', cb: 'onOpenJourneyActivation' },
  { id: 'badgeManager', label: 'Badges', icon: '\u{1F3F7}️', testId: 'admin-open-badge-manager', cb: 'onOpenBadgeManager' },
  { id: 'adminUsers', label: 'Admin Users', icon: '\u{1F465}', testId: 'admin-open-admin-users', cb: 'onOpenAdminUsers' },
  { id: 'registeredUsers', label: 'Users', icon: '\u{1F393}', testId: 'admin-open-registered-users', cb: 'onOpenRegisteredUsers' },
  { id: 'foundingTesters', label: 'Founding Testers', icon: '\u{1F3DB}\u{FE0F}', testId: 'admin-open-founding-testers', cb: 'onOpenFoundingTesters' },
  { id: 'testimonials', label: 'Testimonials', icon: '\u{2B50}', testId: 'admin-open-testimonials', cb: 'onOpenTestimonials' },
  { id: 'caseStudies', label: 'Case Studies', icon: '\u{1F4DA}', testId: 'admin-open-case-studies', cb: 'onOpenCaseStudies', featureFlag: 'caseStudyAdminEnabled' },
  { id: 'seoHealth', label: 'SEO Health', icon: '\u{1F50D}', testId: 'admin-open-seo-health', cb: 'onOpenSeoHealth' },
  { id: 'releaseNotes', label: 'Release Notes', icon: '\u{1F4C4}', testId: 'admin-open-release-notes', cb: 'onOpenReleaseNotes' },
  { id: 'feedbackHub', label: 'Feedback', icon: '\u{1F4AC}', testId: 'admin-open-feedback-hub', cb: 'onOpenFeedbackHub' },
  { id: 'dataTools', label: 'Data Tools', icon: '\u{1F6E0}️', testId: 'admin-open-data-tools', cb: 'onOpenDataTools', stagingOnly: true },
];

// Style values copied from AdminScreen's ribbon (bridge-bg #1a1a2e,
// accent #1565c0, red #d83a3a) so the footer looks identical on the hub.
const CSS = `
  .qa-spacer{height:52px;}
  .qa-ribbon{position:fixed;bottom:0;left:0;right:0;background:#1a1a2e;z-index:55;padding:9px 18px;display:flex;align-items:center;gap:9px;box-shadow:0 -4px 18px rgba(16,16,40,0.18);overflow-x:auto;}
  .qa-label{font-size:0.68rem;font-weight:800;letter-spacing:1px;color:#7a7a9a;text-transform:uppercase;white-space:nowrap;margin-right:4px;}
  .qa-btn{display:flex;align-items:center;gap:7px;padding:7px 13px;background:rgba(255,255,255,0.06);border:1px solid #33334e;border-radius:9px;color:#dcdcec;font-size:0.8rem;font-weight:600;cursor:pointer;white-space:nowrap;transition:all .12s;}
  .qa-btn:hover{background:rgba(255,255,255,0.14);border-color:#5a5a82;}
  .qa-btn .qa-badge{font-size:0.66rem;font-weight:800;color:#fff;background:#d83a3a;border-radius:999px;padding:1px 7px;}
  .qa-btn.qa-primary{background:#1565c0;border-color:#1565c0;color:#fff;}
  .qa-btn.qa-active{background:rgba(255,255,255,0.20);border-color:#8888b0;color:#fff;}
`;

export default function AdminQuickActions({ onAction, reportCount = 0, activeView = null }) {
  const tools = useMemo(
    () => QUICK_ACTION_TOOLS.filter(
      (t) => (!t.stagingOnly || IS_STAGING) && (!t.featureFlag || isFeatureEnabled(t.featureFlag))
    ),
    []
  );

  return (
    <>
      {/* In-flow spacer so document-flow pages aren't hidden behind the
          fixed ribbon. Screens with internal 100vh scroll areas float the
          ribbon above their bottom edge, same as Mission Control did. */}
      <div className="qa-spacer" aria-hidden="true" />
      <div className="qa-ribbon" data-testid="admin-quick-actions">
        <style>{CSS}</style>
        <span className="qa-label">Quick actions</span>
        {tools.map((t) => {
          const active = VIEW_BY_TOOL[t.id] === activeView;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={t.testId}
              className={
                'qa-btn' +
                (t.primary && !active ? ' qa-primary' : '') +
                (active ? ' qa-active' : '')
              }
              onClick={() => onAction(t.id)}
            >
              {t.icon} {t.label}
              {t.badge && reportCount > 0 && (
                <span data-testid="admin-report-count-badge" className="qa-badge">{reportCount}</span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
